import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import LandingPage from './components/utils/LandingPage';
import ProjectDescription from './components/utils/ProjectDescription';
import LoginPage from './components/utils/LoginPage';
import ResetPasswordPage from './components/utils/ResetPasswordPage';
import AppWithSidebar, { Tab } from './components/utils/TabRouter';


const AppRouter: React.FC = () => {
  const location = useLocation();
  const isResetPasswordRoute = React.useMemo(() => {
    const hash = location.hash || '';
    const search = new URLSearchParams(location.search || '');
    const hasResetQuery = Boolean(search.get('uid') && search.get('token'));
    return location.pathname === '/reset-password' || hash.startsWith('#/reset-password') || (location.pathname === '/' && hasResetQuery);
  }, [location.hash, location.pathname, location.search]);
  const [showLanding, setShowLanding] = React.useState<boolean>(() => {
    try {
      // If the URL contains a hash (app tab) or a non-root pathname, assume user is past landing
      const { pathname, hash } = window.location;
      if ((hash && hash.length > 1) || (pathname && pathname !== '/' && pathname !== '/project-description')) return false;
    } catch (e) {
      // ignore and fallback
    }
    return true;
  });
  const [loggedInUser, setLoggedInUser] = React.useState<any>(null);
  const [activeTab, setActiveTab] = React.useState<Tab>(Tab.DASHBOARD);

  // On mount, restore logged-in user and active tab if present in localStorage or URL
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem('AUTH_TOKEN');
      if (stored) {
        const parsed = JSON.parse(stored);
        setLoggedInUser(parsed);
        setShowLanding(false);
      }

      // prefer explicit URL hash for tab, otherwise fallback to stored active tab
      const hash = window.location.hash.replace('#', '');
      if (hash && Object.values(Tab).includes(hash as Tab)) {
        setActiveTab(hash as Tab);
      } else {
        const savedTab = localStorage.getItem('ACTIVE_TAB');
        if (savedTab && Object.values(Tab).includes(savedTab as Tab)) {
          setActiveTab(savedTab as Tab);
          // also update the URL so TabRouter picks it up
          if (window.location.hash !== `#${savedTab}`) window.location.hash = `#${savedTab}`;
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Persist active tab changes so refresh returns user to same page
  React.useEffect(() => {
    try {
      localStorage.setItem('ACTIVE_TAB', String(activeTab));
    } catch (e) {}
  }, [activeTab]);

  const handleNavigateToLogin = () => setShowLanding(false);
  // Debug: log login payload
  const handleLoginSuccessDebug = (user: any) => {
    try { console.debug('[AppRouter] login success user:', user); } catch (e) {}
    setLoggedInUser(user);
  };
  const handleLoginSuccess = (user: any) => {
    try { console.debug('[AppRouter] login success (nav) user:', user); } catch (e) {}
    setLoggedInUser(user);
    setShowLanding(false);
  };

  if (isResetPasswordRoute) {
    return <ResetPasswordPage />;
  }

  if (showLanding) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage onNavigateToLogin={handleNavigateToLogin} />} />
        <Route path="/project-description" element={<ProjectDescription />} />
      </Routes>
    );
  }

  if (!loggedInUser) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <AppWithSidebar
      activeTab={activeTab}
      setActiveTab={(tab: string) => setActiveTab(tab as Tab)}
      loading={false}
      isLoggedIn={true}
      user={loggedInUser}
      setLoggedInUser={setLoggedInUser}
    />
  );
};

export default AppRouter;
